import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { notification, Popover, Modal } from "antd";
import mailClientAPI from "../../api/mail-client-api";

const FOLDERS = [
  { id: "inbox", name: "Inbox", icon: "inbox" },
  { id: "sent", name: "Sent", icon: "send" },
  { id: "drafts", name: "Drafts", icon: "drafts" },
  { id: "scheduled", name: "Scheduled", icon: "schedule" },
  { id: "archived", name: "Archived", icon: "archive" },
  { id: "spam", name: "Spam", icon: "report" },
  { id: "trash", name: "Trash", icon: "delete" },
];

// Color options with hex values
const COLORS = [
  { name: "gray", class: "bg-gray-500", hex: "#6B7280" },
  { name: "red", class: "bg-red-500", hex: "#EF4444" },
  { name: "orange", class: "bg-orange-500", hex: "#F97316" },
  { name: "yellow", class: "bg-yellow-500", hex: "#EAB308" },
  { name: "green", class: "bg-green-500", hex: "#22C55E" },
  { name: "teal", class: "bg-teal-500", hex: "#14B8A6" },
  { name: "blue", class: "bg-blue-500", hex: "#3B82F6" },
  { name: "indigo", class: "bg-indigo-500", hex: "#6366F1" },
  { name: "purple", class: "bg-purple-500", hex: "#A855F7" },
  { name: "pink", class: "bg-pink-500", hex: "#EC4899" },
];

export const Sidebar = ({ isOpen = false, onClose, currentFolder, onSelectFolder, onCompose, unreadCounts = {}, totalCounts = {}, labels = [], labelsLoading = false, refreshLabels }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAddingLabel, setIsAddingLabel] = useState(false);
  const [isEditingLabel, setIsEditingLabel] = useState(null);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState(COLORS[6].hex); // Default to blue
  const [popoverOpen, setPopoverOpen] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState(null);
  const addLabelPopupRef = useRef(null);

  // Handle click outside for popup
  useEffect(() => {
    function handleClickOutside(event) {
      if (addLabelPopupRef.current && !addLabelPopupRef.current.contains(event.target)) {
        setIsAddingLabel(false);
        setIsEditingLabel(null);
        setNewLabelName("");
        setNewLabelColor(COLORS[6].hex);
      }
    }
    if (isAddingLabel || isEditingLabel) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAddingLabel, isEditingLabel]);

  // Check if a label is active
  const isLabelActive = (labelId) => {
    return location.pathname === `/mailboxes/label/${labelId}`;
  };

  // Handle label click
  const handleLabelClick = (labelId) => {
    navigate(`/mailboxes/label/${labelId}`);
    if (onClose) {
      onClose(); // Close drawer on mobile after selecting label
    }
  };

  // Handle add label
  const handleAddLabel = async () => {
    if (!newLabelName.trim()) {
      notification.error({
        message: "Label name is required",
        placement: "bottomLeft",
      });
      return;
    }

    try {
      const response = await mailClientAPI.createLabel(newLabelName.trim(), newLabelColor);
      notification.success({
        message: "Label created successfully",
        placement: "bottomLeft",
      });
      setNewLabelName("");
      setNewLabelColor(COLORS[6].hex);
      setIsAddingLabel(false);
      if (refreshLabels) {
        await refreshLabels();
      }
    } catch (error) {
      console.error("Error creating label:", error);
      notification.error({
        message: error.response?.data?.Output?.message || "Failed to create label",
        placement: "bottomLeft",
      });
    }
  };

  // Handle edit label
  const handleEditLabel = async () => {
    if (!newLabelName.trim()) {
      notification.error({
        message: "Label name is required",
        placement: "bottomLeft",
      });
      return;
    }

    try {
      await mailClientAPI.updateLabel(isEditingLabel, newLabelName.trim(), newLabelColor);
      notification.success({
        message: "Label updated successfully",
        placement: "bottomLeft",
      });
      setNewLabelName("");
      setNewLabelColor(COLORS[6].hex);
      setIsEditingLabel(null);
      if (refreshLabels) {
        await refreshLabels();
      }
    } catch (error) {
      console.error("Error updating label:", error);
      notification.error({
        message: error.response?.data?.Output?.message || "Failed to update label",
        placement: "bottomLeft",
      });
    }
  };

  // Handle delete label confirmation
  const showDeleteModal = (labelId) => {
    setLabelToDelete(labelId);
    setDeleteModalOpen(true);
    setPopoverOpen({ ...popoverOpen, [labelId]: false });
  };

  // Handle delete label
  const handleDeleteLabel = async () => {
    if (!labelToDelete) return;

    try {
      await mailClientAPI.deleteLabel(labelToDelete);
      notification.success({
        message: "Label deleted successfully",
        placement: "bottomLeft",
      });
      setDeleteModalOpen(false);
      setLabelToDelete(null);
      if (refreshLabels) {
        await refreshLabels();
      }
    } catch (error) {
      console.error("Error deleting label:", error);
      notification.error({
        message: error.response?.data?.Output?.message || "Failed to delete label",
        placement: "bottomLeft",
      });
    }
  };

  // Open edit popup
  const openEditPopup = (label) => {
    setIsEditingLabel(label.id);
    setNewLabelName(label.name);
    setNewLabelColor(label.color);
    setPopoverOpen({ ...popoverOpen, [label.id]: false });
  };

  return (
    <>
      {/* Overlay Backdrop - Visible on mobile when drawer is open */}
      <div 
        className={`fixed inset-0 bg-black/20 backdrop-blur-[2px] z-30 transition-all duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] md:hidden ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={onClose}
      />

      {/* Sidebar / Drawer */}
      <aside 
        className={`w-64 bg-gray-50 h-full flex flex-col p-4 border-r border-gray-200
          fixed md:static top-0 left-0 z-40
          transform transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)]
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Brand */}
        <div className="flex items-center justify-between mb-6 px-2">
          <div className="flex items-center">
            <div className="w-12 h-12 flex items-center justify-center mr-3">
              <img src="/logo512.png" alt="Grigora" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-xl text-gray-800 tracking-tight">Grigora</span>
          </div>
          {/* Close button - visible on mobile only */}
          <button 
            onClick={onClose}
            className="md:hidden p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <span className="material-icons-outlined text-2xl">close</span>
          </button>
        </div>

      {/* Compose Button */}
      <button
        className="h-14 pl-4 pr-6 rounded-2xl shadow-lg shadow-grigora-500/30 mb-6 ml-1 bg-grigora-500 text-white hover:bg-grigora-600 hover:scale-105 hover:shadow-grigora-500/50 transition-all duration-300 ease-out flex items-center gap-3 group w-fit"
        onClick={onCompose}
      >
        <span className="material-icons-outlined text-2xl group-hover:rotate-12 transition-transform">edit</span>
        <span className="font-semibold text-lg">Compose</span>
      </button>

      <div className="flex-1 flex flex-col overflow-y-auto">
        <nav className="space-y-1 mb-6">
          {FOLDERS.map((folder) => {
            var isActive = currentFolder === folder.id;
            const unreadCount = unreadCounts[folder.id] || 0;
            const totalCount = totalCounts[folder.id] || 0;
            
            // For drafts and scheduled, use total count instead of unread count
            const displayCount = (folder.id === "drafts" || folder.id === "scheduled") ? totalCount : unreadCount;

            const isAnyLabelActive = location.pathname.includes(`/mailboxes/label/`);

            isActive = isActive && !isAnyLabelActive;

            return (
              <button
                key={folder.id}
                onClick={() => onSelectFolder(folder.id)}
                className={`w-full flex items-center justify-between px-4 py-2 rounded-r-full text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-grigora-100 text-grigora-700 font-bold"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                <div className="flex items-center">
                  <span
                    className={`material-icons-outlined mr-4 ${isActive ? "text-grigora-600" : "text-gray-500"}`}
                  >
                    {folder.icon}
                  </span>
                  {folder.name}
                </div>
                {(folder.id === "drafts" || folder.id === "scheduled") ? (
                  displayCount > 0 && (
                    // For drafts and scheduled, show count if > 0
                    <span className={`text-xs ${isActive ? "font-bold" : "font-semibold"}`}>{displayCount}</span>
                  )
                ) : (
                  // For other folders, only show if unread count > 0
                  unreadCount > 0 && (
                    <span className={`text-xs ${isActive ? "font-bold" : "font-semibold"}`}>{displayCount}</span>
                  )
                )}
              </button>
            );
          })}
        </nav>

        {/* Labels Section */}
        <div className="px-2 relative">
          <div className="flex items-center justify-between mb-2 group cursor-pointer">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Labels</h3>
            <button
              className="text-gray-400 hover:text-grigora-500 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Add Label"
              onClick={(e) => {
                e.stopPropagation();
                setIsAddingLabel(true);
              }}
            >
              <span className="material-icons-outlined text-sm">add</span>
            </button>
          </div>

          {/* Add/Edit Label Popup */}
          {(isAddingLabel || isEditingLabel) && (
            <div
              ref={addLabelPopupRef}
              className="absolute left-4 right-4 top-8 bg-white shadow-xl border border-gray-200 rounded-lg p-3 z-50 animate-fade-in"
            >
              <h4 className="text-xs font-bold text-gray-700 mb-2">
                {isEditingLabel ? "Edit Label" : "New Label"}
              </h4>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm mb-3 focus:ring-2 focus:ring-grigora-500 outline-none"
                placeholder="Label name"
                autoFocus
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    isEditingLabel ? handleEditLabel() : handleAddLabel();
                  }
                }}
              />

              <div className="mb-3">
                <div className="text-xs text-gray-400 mb-1.5 font-medium">Color</div>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c.name}
                      type="button"
                      className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${
                        newLabelColor === c.hex
                          ? "ring-2 ring-offset-1 ring-gray-400 scale-110"
                          : ""
                      }`}
                      style={{ backgroundColor: c.hex }}
                      onClick={() => setNewLabelColor(c.hex)}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                  onClick={() => {
                    setIsAddingLabel(false);
                    setIsEditingLabel(null);
                    setNewLabelName("");
                    setNewLabelColor(COLORS[6].hex);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="text-xs bg-grigora-500 text-white px-3 py-1 rounded hover:bg-grigora-600 font-medium"
                  onClick={isEditingLabel ? handleEditLabel : handleAddLabel}
                >
                  {isEditingLabel ? "Save" : "Add"}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-1">
            {labelsLoading ? (
              <div className="text-xs text-gray-400 px-2 py-1.5">Loading labels...</div>
            ) : labels.length === 0 ? (
              <div className="text-xs text-gray-400 px-2 py-1.5"></div>
            ) : (
              labels.map((label) => {
                const isActive = isLabelActive(label.id);
                
                const menuContent = (
                  <div className="min-w-[120px]">
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded"
                      onClick={() => {
                        openEditPopup(label);
                      }}
                    >
                      <span className="material-icons-outlined text-base">edit</span>
                      Edit
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded"
                      onClick={() => {
                        showDeleteModal(label.id);
                      }}
                    >
                      <span className="material-icons-outlined text-base">delete</span>
                      Delete
                    </button>
                  </div>
                );

                return (
                  <div key={label.id} className={`relative group flex items-center rounded-lg ${isActive ? "bg-grigora-100 text-grigora-700 font-bold" : "text-gray-600 hover:bg-gray-100"}`}>
                    <button
                      onClick={() => handleLabelClick(label.id)}
                      className={`flex-1 flex items-center px-2 py-1.5 text-sm rounded-lg transition-colors`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full mr-3"
                        style={{ backgroundColor: label.color }}
                      ></span>
                      <span className="truncate">{label.name}</span>
                    </button>
                    <Popover
                      content={menuContent}
                      trigger="click"
                      placement="rightTop"
                      open={popoverOpen[label.id]}
                      onOpenChange={(open) => {
                        setPopoverOpen({ ...popoverOpen, [label.id]: open });
                      }}
                      rootClassName="label-menu-popover"
                    >
                      <button
                        className={`ml-1 p-1 material-icons-outlined text-sm opacity-0 group-hover:opacity-100 hover:text-gray-600 rounded transition-opacity ${isActive ? "text-grigora-700" : "text-gray-300"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        more_vert
                      </button>
                    </Popover>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Delete Label Modal */}
      <Modal
        open={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setLabelToDelete(null);
        }}
        onOk={handleDeleteLabel}
        okText="Yes, Delete"
        cancelText="No, Cancel"
        okButtonProps={{ danger: true }}
        title="Delete Label"
      >
        <p>Are you sure you want to delete this label? This action cannot be undone.</p>
      </Modal>
      </aside>
    </>
  );
};

